import React from 'react';
import { Button } from '../ui/Button';
import { CheckIcon } from 'lucide-react';
interface PricingTier {
  name: string;
  price: string;
  period?: string;
  description: string;
  features: string[];
  cta: string;
  highlighted?: boolean;
}
const tiers: PricingTier[] = [{
  name: 'Solo',
  price: '$29',
  period: '/mo',
  description: 'For individual power users',
  features: ['5 active streams', '1,000 agent executions/mo', 'Basic context store (1GB)', 'Markdown export', 'Email support'],
  cta: 'Start Free Trial'
}, {
  name: 'Team',
  price: '$99',
  period: '/seat/mo',
  description: 'For collaborative teams',
  features: ['Unlimited streams', '10,000 agent executions/seat', 'Shared context stores (50GB)', 'Org-wide search', 'All export formats', 'Priority support', 'SSO (SAML)'],
  cta: 'Start Free Trial',
  highlighted: true
}, {
  name: 'Enterprise',
  price: 'Custom',
  description: 'For large organizations',
  features: ['Everything in Team', 'Unlimited executions', 'Custom integrations', 'Audit logs & compliance', 'Dedicated support', 'SLA guarantee', 'On-premise option'],
  cta: 'Contact Sales'
}];
function PricingCard({
  tier
}: {
  tier: PricingTier;
}) {
  return <div className={`
        relative p-8 rounded-2xl border flex flex-col overflow-visible
        ${tier.highlighted ? 'bg-surface-700 border-primary-500/50 shadow-xl shadow-primary-500/10 scale-105' : 'bg-surface-800 border-surface-600'}
      `}>
      {tier.highlighted && <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
          <span className="px-3 py-1 bg-surface-900 text-primary-400 text-xs font-medium rounded-full border border-primary-500/50">
            Most Popular
          </span>
        </div>}

      <div className="mb-6">
        <h3 className="text-xl font-semibold text-white mb-2">{tier.name}</h3>
        <div className="flex items-baseline gap-1">
          <span className="text-4xl font-bold text-white">{tier.price}</span>
          {tier.period && <span className="text-gray-400">{tier.period}</span>}
        </div>
        <p className="text-gray-400 mt-2">{tier.description}</p>
      </div>

      <ul className="space-y-3 mb-8 flex-1">
        {tier.features.map((feature, index) => <li key={index} className="flex items-start gap-3">
            <CheckIcon className="w-5 h-5 text-success-400 flex-shrink-0 mt-0.5" />
            <span className="text-gray-300 text-sm">{feature}</span>
          </li>)}
      </ul>

      <Button variant={tier.highlighted ? 'primary' : 'secondary'} className="w-full">
        {tier.cta}
      </Button>
    </div>;
}
export function Pricing() {
  return <section className="py-32 px-6 bg-surface-800/50">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-16">
          <p className="text-primary-400 font-medium mb-4">Pricing</p>
          <h2 className="text-4xl font-bold text-white mb-6">
            Simple, transparent pricing
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Start free, scale as you grow. No hidden fees, no surprises.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 items-start pt-6 overflow-visible">
          {tiers.map(tier => <PricingCard key={tier.name} tier={tier} />)}
        </div>

        <p className="text-center text-gray-500 mt-12">
          All plans include a 14-day free trial. No credit card required.
        </p>
      </div>
    </section>;
}